import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Mail, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { extractApiErrorMessage } from '@/lib/api-error';
import { usePmProperty } from '@/hooks/usePmProperty';
import {
  createEnterpriseTenant,
  listEnterpriseTenants,
  type CreateEnterpriseTenantBody,
} from '@/services/api.client';

interface FormState {
  companyName: string;
  unitReference: string;
  floor: string;
  adminFullName: string;
  adminEmail: string;
  adminPhone: string;
}

const INITIAL_FORM: FormState = {
  companyName: '',
  unitReference: '',
  floor: '',
  adminFullName: '',
  adminEmail: '',
  adminPhone: '',
};

/**
 * CompaniesPage — building manager view of the tenant companies that occupy
 * the property. Lists each tenant with its admin contact, employee count and
 * reception hours; an "Add Company" dialog wires the BM to invite a new
 * tenant in one step (creates the unit + admin user + role atomically on
 * the server). ADR 009 slice 3.
 */
export function CompaniesPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);

  const tenantsQuery = useQuery({
    queryKey: ['pm', 'enterprise-tenants', propertyId],
    queryFn: () => listEnterpriseTenants(propertyId),
    enabled: !!propertyId,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateEnterpriseTenantBody) => createEnterpriseTenant(propertyId, body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['pm', 'enterprise-tenants', propertyId] });
      toast.success(`${result.companyName} added — invite sent to ${result.adminEmail}`);
      setDialogOpen(false);
      setForm(INITIAL_FORM);
    },
    onError: (err) => {
      toast.error(extractApiErrorMessage(err, 'Failed to add company'));
    },
  });

  function updateField(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit() {
    if (!form.companyName.trim() || !form.adminEmail.trim() || !form.adminFullName.trim()) {
      toast.error('Company name, admin name, and admin email are required');
      return;
    }
    createMutation.mutate({
      companyName: form.companyName.trim(),
      unitReference: form.unitReference.trim() || form.companyName.trim(),
      ...(form.floor.trim() && { floor: form.floor.trim() }),
      adminFullName: form.adminFullName.trim(),
      adminEmail: form.adminEmail.trim().toLowerCase(),
      ...(form.adminPhone.trim() && { adminPhone: form.adminPhone.trim() }),
    });
  }

  const tenants = tenantsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-[var(--text-primary)]">Companies</h1>
          <p className="text-sm text-[var(--text-tertiary)]">
            Tenant companies that occupy this building. Adding a company invites their admin to set
            up the directory and reception details.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="size-4 mr-1.5" />
          Add Company
        </Button>
      </div>

      <div className="rounded-md border">
        {tenantsQuery.isLoading ? (
          <div className="p-6">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <Building2 className="size-8 text-[var(--text-tertiary)]" />
            <p className="text-sm font-medium text-[var(--text-primary)]">No companies yet</p>
            <p className="text-sm text-[var(--text-tertiary)] max-w-xs">
              Add your first tenant company to start routing visitors and worker passes through its
              admin.
            </p>
            <Button variant="outline" className="mt-2" onClick={() => setDialogOpen(true)}>
              <Plus className="size-4 mr-1.5" />
              Add Company
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Floor</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead className="text-right">Employees</TableHead>
                <TableHead>Reception Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} data-testid={`company-row-${t.id}`}>
                  <TableCell className="font-medium">
                    {t.companyName ?? <span className="italic text-[var(--text-tertiary)]">—</span>}
                  </TableCell>
                  <TableCell className="text-[var(--text-secondary)]">
                    {t.floor ?? <span className="text-[var(--text-tertiary)]">—</span>}
                  </TableCell>
                  <TableCell>
                    {t.contactAdmin ? (
                      <div className="flex flex-col">
                        <span className="text-sm">{t.contactAdmin.fullName}</span>
                        <span className="text-xs text-[var(--text-tertiary)]">
                          {t.contactAdmin.email}
                        </span>
                      </div>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-[var(--text-tertiary)]">
                        <Mail className="size-3" />
                        Pending invite
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="inline-flex items-center gap-1 text-sm">
                      <Users className="size-3 text-[var(--text-tertiary)]" />
                      {t.employeeCount ?? 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-[var(--text-secondary)]">
                    {t.receptionHours ?? <span className="text-[var(--text-tertiary)]">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="company-name">Company Name *</Label>
              <Input
                id="company-name"
                value={form.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder="Acme Corporation"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="unit-reference">Unit Reference</Label>
                <Input
                  id="unit-reference"
                  value={form.unitReference}
                  onChange={(e) => updateField('unitReference', e.target.value)}
                  placeholder="e.g. T2-12"
                />
                <p className="text-xs text-[var(--text-tertiary)]">Defaults to the company name.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="floor">Floor</Label>
                <Input
                  id="floor"
                  value={form.floor}
                  onChange={(e) => updateField('floor', e.target.value)}
                  placeholder="12"
                />
              </div>
            </div>
            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Tenant Admin</p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-name">Full Name *</Label>
                  <Input
                    id="admin-name"
                    value={form.adminFullName}
                    onChange={(e) => updateField('adminFullName', e.target.value)}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="admin-email">Email *</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={form.adminEmail}
                    onChange={(e) => updateField('adminEmail', e.target.value)}
                    placeholder="jane@acme.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="admin-phone">Phone</Label>
                  <Input
                    id="admin-phone"
                    type="tel"
                    value={form.adminPhone}
                    onChange={(e) => updateField('adminPhone', e.target.value)}
                    placeholder="+254712345678"
                  />
                  <p className="text-xs text-[var(--text-tertiary)]">
                    Required for SMS notifications to company admin
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Sending invite…' : 'Add Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
